<template>
    <Dialog v-model:visible="visible"  header="Add New Deal" style="{ height: '90rem' }" modal
    class="w-full 2xl:w-4/5 modal-content" @hide="closeModal">
        <div v-if="!selectedAdvertiser" class="p-4">
            <div class="field">
                <div class="flex items-center gap-2 mb-2">
                    <i class="pi pi-briefcase text-sm"></i>
                    <label for="advertiser" class="block text-sm font-medium">Select Advertiser</label>
                </div>
                <Select 
                    v-model="selectedAdvertiser" 
                    :options="advertisers" 
                    :loading="advertisersLoading"
                    optionLabel="name" 
                    optionValue="id"
                    placeholder="Search advertiser..." 
                    class="w-full"
                    :filter="true"
                    :filterMatchMode="'contains'"
                    :filterPlaceholder="'Type to search...'"
                    fluid
                    @change="onAdvertiserSelect"
                >
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option.name }}</span>
                        </div>
                    </template>
                    <template #filtertemplate="{ filterOptions }">
                        <div class="p-input-icon-left w-full">
                            <i class="pi pi-search"></i>
                            <InputText 
                                v-bind="filterOptions" 
                                placeholder="Search advertiser..." 
                                class="w-full"
                            />
                        </div>
                    </template>
                </Select>
            </div>
        </div>
        <div v-else-if="selectedAdvertiserData === null" class="flex justify-center items-center p-8">
            <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="3" />
            <span class="ml-3 text-gray-600">Loading advertiser details...</span>
        </div>
        <div v-else-if="selectedAdvertiserData">
            <EditAdvertiser 
                :isEditing="true" 
                :advertiserData="selectedAdvertiserData"
                :presetData="{
                    userId: props.userId,
                    siteType: props.site.type,
                    marketName: props.site.country
                }"
                @dealAdded="dealAdded" 
            />
        </div>
        <template #footer>
            <div class="flex justify-end gap-3">
                <Button label="Cancel" icon="pi pi-times" @click="closeModal" text />
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref } from 'vue';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import AdvertiserService from '@/services/AdvertiserService';
import EditAdvertiser from '@/views/Advertisers/Settings/AdvertiserData.vue';
import ProgressSpinner from 'primevue/progressspinner';

const props = defineProps({
    userId: {
        type: String,
        required: true
    },
    site: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['close', 'save']);
const visible = ref(false);
const selectedAdvertiser = ref(null);
const selectedAdvertiserData = ref(null);
const advertisers = ref([]);

const resetState = () => {
    selectedAdvertiser.value = null;
    selectedAdvertiserData.value = null;
    advertisersLoading.value = false;
    advertisers.value = [];
};

const closeModal = () => {
    resetState();
    visible.value = false;
    emit('close');
};

const open = () => {
    visible.value = true;
    fetchAdvertisers();

};

const dealAdded = (newDeal) => {
    console.log('Deal added', newDeal);
    emit('save', {
        advertiserId: selectedAdvertiser.value,
        newDeal
    });
    closeModal();
}

const advertisersLoading = ref(true);
const fetchAdvertisers = async () => {
    try {
        advertisersLoading.value = true;
        const response = await AdvertiserService.getAll();
        // Filter out advertisers that are already linked to this site
        advertisers.value = response.data.filter(advertiser => 
            !props.site.advertisers?.some(linkedAdvertiser => 
                linkedAdvertiser.advertiserId._id === advertiser.id
            )
        );
    } catch (error) {
        console.error('Error fetching advertisers:', error);
    } finally {
        advertisersLoading.value = false;
    }
};

const fetchAdvertiserData = async (id) => {
    try{
        advertisersLoading.value = true;
        const response = await AdvertiserService.getSingleAdvertiserForAdminSettings(id);
        selectedAdvertiserData.value = response.data;
    } catch (error) {
        console.error('Error fetching advertiser data:', error);
    } finally {
        advertisersLoading.value = false;
    }
};

const onAdvertiserSelect = () => {
    fetchAdvertiserData(selectedAdvertiser.value);
};

defineExpose({ open });
</script> 